<template>
  <div class="space-y-6">
    <div class="grid justify-between gap-2 md:flex">
      <p class="text-xl font-bold">{{ car.vehicleType.make.name }} {{ car.vehicleType.model }}</p>

      <div class="flex justify-end gap-6">
        <FeatureIcon :icon="hpIcon" :label="t('hp', { power: car.vehicleType.power })" />
        <FeatureIcon
          :icon="accelerationIcon"
          :label="t('acceleration', { acceleration: car.vehicleType.accelaration })"
        />
        <FeatureIcon
          :icon="seatsIcon"
          :label="
            t('seats', {
              seats: formatSeats(seats, additionalSeats),
            })
          "
        />
        <FeatureIcon
          :icon="minAgeIcon"
          :label="t('minAgeIcon', { minAge: car.vehicleType.minAge })"
        />
      </div>
    </div>

    <div class="grid gap-6 md:grid-cols-2 md:items-center">
      <img :src="imageUrl" class="max-h-44" />
      <FeatureList :title="t('featuresIncluded')" :features="featuresIncluded" class="mb-4" />
    </div>

    <div class="grid gap-6 md:grid-cols-2">
      <FeatureList :title="t('otherFeatures')" :features="otherFeatures" />
      <FeatureList :title="t('rentRequirements')" :features="rentRequirements" />
    </div>

    <Divider />

    <div v-if="hasTransfers">
      <Transfers
        v-if="pickupLocation && dropoffLocation"
        :car="car"
        :create-booking-props="createBookingProps"
      />

      <Divider />
    </div>

    <div v-if="hasEmergencyService">
      <EmergencyService :car="car" :create-booking-props="createBookingProps" />

      <Divider />
    </div>

    <Extras
      :price-calculation="pricing!"
      :period-index="longTermPricing ? 0 : undefined"
      :is-loading="isLoading"
      @update:booked-extras="emit('updateBookedExtras', $event)"
      @update:custom-booked-extras="emit('updateCustomBookedExtras', $event)"
      @update:is-valid="isExtrasFormValid = $event"
    />

    <template v-if="longTermPricing">
      <Divider />
      <PeriodTable :pricing="longTermPricing" condensed />
    </template>

    <Divider />

    <div v-if="!insuranceCase?.id">
      <Checkbox
        :model-value="isAgencyBooking"
        :label="t('isAgencyBooking')"
        @change="(value) => $emit('updateIsAgencyBooking', value)"
      />

      <Divider />
    </div>
    <div v-if="recentNumbers?.length" class="flex items-center gap-2">
      <Select
        v-model="selectedPhoneNumber"
        :options="recentNumbers"
        data-testid="select-phone-number"
      />
      <Select v-model="selectedLinkStep" :options="linkStepOptions" class="" />
      <Select
        v-model="selectedLocale"
        :options="localeOptions"
        data-testid="select-sms-locale"
        class="w-36"
      />
      <CVButton
        variant="secondary"
        :disabled="!selectedPhoneNumber"
        :is-loading="isSendingLink"
        @click="sendLink"
      >
        {{ t('sendSmsLink') }}
      </CVButton>
    </div>
    <div class="grid items-end justify-between gap-4 md:flex">
      <LocationsAndTimesInfo
        v-if="pickupLocation && dropoffLocation"
        :create-booking-props="createBookingProps"
      />
      <div class="grid items-center gap-4 md:flex">
        <TotalPrice :total-price="pricing!.totalPrice" />
        <CVButton
          variant="default"
          :is-loading="isLoading"
          :disabled="!isExtrasFormValid"
          @click="$emit('continue')"
          >{{ t('continue') }}</CVButton
        >
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { Car } from '@/entities/cars/car.entity';
import { computed, ref, toRef } from 'vue';
import accelerationIcon from '@/assets/car-feature-icons/acceleration.png';
import hpIcon from '@/assets/car-feature-icons/hp.png';
import minAgeIcon from '@/assets/car-feature-icons/min-age.png';
import seatsIcon from '@/assets/car-feature-icons/seats.png';
import { formatSeats } from '@/utils/seats';
import FeatureIcon from './FeatureIcon.vue';
import FeatureList from './FeatureList.vue';
import { useI18n } from 'vue-i18n';
import { formatCurrency } from '@/utils/format-numbers';
import { useTrueCarAttributes } from '@/hooks/use-true-car-attributes';
import Extras from '@/components/extras/ExtrasForm.vue';
import Divider from '@/components/Divider.vue';
import Checkbox from '@/components/Checkbox.vue';
import Transfers from './Transfers.vue';
import TotalPrice from '../../TotalPrice.vue';
import LocationsAndTimesInfo from '../../LocationsAndTimesInfo.vue';
import type { CreateBookingProps } from '@/views/bookings/booking-create/components/use-create-booking-props';
import { exclusiveBookingLocation } from '@/utils/booking-location-to-location-dto';
import { useRecentPhoneNumbers, useSendBookingLink } from '@/queries/use-phone';
import Select from '@/components/Select.vue';
import {
  BookingLinkStep,
  Local,
  type SendBookingLinkParamsDto,
  VehicleExtraType,
} from '@carvia/ros-client-types';
import EmergencyService from './EmergencyService.vue';

const props = defineProps<{
  car: Car;
  createBookingProps: CreateBookingProps;
  isAgencyBooking: boolean;
  isLoading?: boolean;
}>();

const {
  pickupLocation,
  dropoffLocation,
  transferReturnPrice,
  transferDeliveryPrice,
  pricing,
  insuranceCase,
} = props.createBookingProps;

import type { BookedExtraSelection } from '@/components/extras/ExtrasForm.vue';
import type { LanguageCode } from '@/entities/auth/local.enum';
import type { UpdateCustomBookedExtraDto } from '@carvia/ros-client-types';
import { getBookedExtras, getCustomBookedExtras, isLongTermPricing } from '@/utils/pricing.utils';
import PeriodTable from '@/components/booking-forms/PeriodTable.vue';

const emit = defineEmits<{
  (e: 'updateBookedExtras', value: BookedExtraSelection[]): void;
  (e: 'updateCustomBookedExtras', value: UpdateCustomBookedExtraDto[] | undefined): void;
  (e: 'updateIsAgencyBooking', value: boolean): void;
  (e: 'continue'): void;
}>();

const { t, locale } = useI18n();

const hasTransfers = computed(() => transferDeliveryPrice.value || transferReturnPrice.value);

const hasEmergencyService = computed(
  () =>
    !!(
      pricing.value &&
      getCustomBookedExtras(pricing.value).find(
        (extra) => extra.customVehicleExtraType === VehicleExtraType.EMERGENCY_SERVICE,
      )
    ),
);

const longTermPricing = computed(() =>
  pricing.value && isLongTermPricing(pricing.value) ? pricing.value : null,
);

const { imageUrl, hasDistanceControl, hasNavigation, transmission, drive, seats, additionalSeats } =
  useTrueCarAttributes(toRef(props, 'car'));

const otherFeatures = computed(() => [
  ...(hasNavigation.value ? [t('navigation')] : []),
  ...(hasDistanceControl.value ? [t('distanceControl')] : []),
  t(`transmissionTypes.${transmission.value}`),
  t(`driveTypes.${drive.value}`),
]);

const featuresIncluded = computed(() => [
  t('insurance'),
  pricing.value!.pricePerExtraKm
    ? pricing.value!.inclusiveKm + ' ' + t('kmIncluded')
    : t('unlimitedKmIncluded'),
]);

const rentRequirements = computed(() => [
  t('deposit', { deposit: formatCurrency(pricing.value!.deposit ?? 0) }),
  t('minAge', { age: props.car.vehicleType.minAge }),
  t('licenceNeededForXYears', {
    duration: props.car.vehicleType.licenceNeededForXYears,
  }),
]);

const { data: recentNumbers } = useRecentPhoneNumbers(true);
const selectedPhoneNumber = ref<string>('');
const isExtrasFormValid = ref(true);
const { mutateAsync: sendBookingLink, isPending: isSendingLink } = useSendBookingLink();
type SendBookingLinkParamsWithLocale = SendBookingLinkParamsDto & { locale?: LanguageCode };
const selectedLinkStep = ref<BookingLinkStep>(BookingLinkStep.BOOK);
const linkStepOptions = computed(() => [
  { label: t('selectedCar'), value: BookingLinkStep.CAR },
  { label: t('checkout'), value: BookingLinkStep.BOOK },
]);
const selectedLocale = ref<LanguageCode>((locale.value as any) ?? 'en');
const localeOptions = computed(() => [
  { label: t('locales.de-DE'), value: 'de' },
  { label: t('locales.en-US'), value: 'en' },
]);

const sendLink = async () => {
  if (!selectedPhoneNumber.value) return;
  const carId = props.car.id;
  const pickup = exclusiveBookingLocation(pickupLocation.value!);
  const dropoff = exclusiveBookingLocation(dropoffLocation.value!);
  const query: SendBookingLinkParamsWithLocale = {
    carId,
    startDate: props.createBookingProps.startDate.value,
    endDate: props.createBookingProps.endDate.value,
    pickupLocation: pickup.location?.placeId ?? undefined,
    dropoffLocation: dropoff.location?.placeId ?? undefined,
    pickupStationId: pickup.station?.id,
    dropoffStationId: dropoff.station?.id,
    extras: [],
    step: selectedLinkStep.value,
    locale: selectedLocale.value,
    customerData: {
      phone: selectedPhoneNumber.value,
      preferredLocal: selectedLocale.value === 'de' ? Local.DE_DE : Local.EN_US,
    },
  };
  for (const extra of props.createBookingProps.pricing.value?.availableExtras ?? []) {
    const selectedOption = props.createBookingProps.pricing.value
      ? getBookedExtras(props.createBookingProps.pricing.value).find(
          (b) => b.vehicleExtraSnapshot.id === extra.id,
        )?.vehicleExtraOptionSnapshot.value
      : undefined;
    if (selectedOption) query.extras.push({ type: extra.type, value: selectedOption });
  }
  await sendBookingLink({ phoneNumber: selectedPhoneNumber.value, params: query });
};
</script>

<i18n lang="json">
{
  "en": {
    "otherFeatures": "Other Features",
    "distanceControl": "Distance Control",
    "featuresIncluded": "The rent includes",
    "insurance": "Fully comprehensive insurance with deductible",
    "kmIncluded": "km included",
    "unlimitedKmIncluded": "Unlimited km included",
    "minAge": "Minimum age {age} years",
    "licenceNeededForXYears": "Driving license possession for at least {duration} years",
    "deposit": "Deposit {deposit} by credit card",
    "rentRequirements": "Requirements for the rental",
    "navigation": "GPS/navigation",
    "isAgencyBooking": "Is booked via an agency",
    "continue": "Continue",
    "hp": "{power} HP",
    "acceleration": "0-100 kph {acceleration}s",
    "seats": "{seats} seats",
    "minAgeIcon": "From {age} years",
    "sendSmsLink": "Send booking link",
    "selectedCar": "Car selected step (no extras)",
    "checkout": "Checkout step (with extras)"
  },
  "de": {
    "otherFeatures": "Weitere Ausstattungsmerkmale",
    "distanceControl": "Parkabstandswarner",
    "featuresIncluded": "Die Miete ist inklusive",
    "insurance": "Vollkaskoversicherung mit SB",
    "kmIncluded": "Freikilometer",
    "unlimitedKmIncluded": "Unbegrenzte Freikilometer",
    "minAge": "Mindestalter {age} Jahre",
    "licenceNeededForXYears": "Führerscheinbesitz seit mindestens {duration} Jahren",
    "deposit": "Kaution {deposit} per Kreditkarte",
    "rentRequirements": "Voraussetzungen für die Miete",
    "navigation": "GPS/Navigation",
    "isAgencyBooking": "Wird über eine Agency gebucht",
    "continue": "Weiter",
    "hp": "{power} PS",
    "acceleration": "0-100 km/h {acceleration}s",
    "seats": "{seats} Sitze",
    "minAgeIcon": "Ab {minAge} Jahren",
    "sendSmsLink": "Buchungslink per SMS senden",
    "selectedCar": "Auto ausgewählt Schritt (ohne Extras)",
    "checkout": "Checkout Schritt (mit Extras)"
  },
  "es": {
    "otherFeatures": "Otros equipamientos",
    "distanceControl": "Asistente de distancia / parking",
    "featuresIncluded": "El alquiler incluye",
    "insurance": "Seguro a todo riesgo con franquicia",
    "kmIncluded": "km incluidos",
    "unlimitedKmIncluded": "Km incluidos ilimitados",
    "minAge": "Edad mínima {age} años",
    "licenceNeededForXYears": "Permiso de conducir con al menos {duration} años de antigüedad",
    "deposit": "Depósito {deposit} con tarjeta de crédito",
    "rentRequirements": "Requisitos del alquiler",
    "navigation": "GPS / navegación",
    "isAgencyBooking": "Reservado a través de una agencia",
    "continue": "Continuar",
    "hp": "{power} CV",
    "acceleration": "0-100 km/h {acceleration} s",
    "seats": "{seats} plazas",
    "minAgeIcon": "Desde {age} años",
    "sendSmsLink": "Enviar enlace de reserva por SMS",
    "selectedCar": "Paso vehículo elegido (sin extras)",
    "checkout": "Paso final (con extras)"
  }
}
</i18n>
